import { useMutation } from "@tanstack/react-query";
import { HCF_USER_EVENTS } from "src/constants/analyticsEvents";
import {
  createNewUser,
  fetchUserCount,
  fetchUsers,
  findUserWithEmail,
  linkNewUser,
  updateUserInfo,
} from "src/containers/facilityUser/api";
import { fillDefaultAlertSettings } from "src/containers/facilityUser/helpers";
import {
  createNotifyRoles,
  type GranularControl,
} from "@src/appV2/FacilityUser/notifications/createNotifyRoles";
import { generateAddEditLogData, generateLogData } from "src/containers/teamMembers/logs";
import {
  teamMemberInitialState,
  teamMemberReducer,
} from "src/containers/teamMembers/teamMemberReducer";
import { getHighestAccessRole } from "src/containers/teamMembers/utils";
import { SessionType } from "src/modules/interface";
import { IFacility, TypeAlertSettings, TypeUserInfo } from "src/modules/myAccount/types";
import { getErrorMessage } from "src/utils/errors";
import { logEvent } from "@src/appV2/lib/analytics";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import { useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_PROFILE } from "src/modules/session";
import { FacilityOnboardingStep, updateFacilityOnboardingSteps } from "src/api/facility";
import { getSignInLink } from "src/utils/user";
import { routes } from "@src/appV2/redesign/utils/routes";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FACILITY_USER_PERMISSIONS } from "@src/constants/facility";
import { isDefined } from "@clipboard-health/util-ts";

export interface ITeamMember {
  name: string;
  firstName: string;
  lastName: string;
  roles: string[];
  phone: string;
  email: string;
  workplacePhone: string;
  _id: string;
  designation: string;
  permissions?: string[];
  alertSettings?: TypeAlertSettings;
  userInfo?: Partial<TypeUserInfo>;
  facilities?: IFacility[];
}

type AddTeamMembersOnboardingUpdateStatus = "idle" | "pending" | "succeeded" | "failed";

type TeamMemberSearchFilter = { name: string } | { email: string } | { phone: string };

const MIN_PHONE_SEARCH_DIGIT_COUNT = 3;
const US_COUNTRY_CODE = "1";
const PHONE_FORMATTING_CHARACTERS_REGEX = /[\s().+-]/g;

function isEmailSearchFilter(filter: string): boolean {
  return filter.includes("@") || /^[^\s@+]+\+[^\s@+]+$/.test(filter);
}

function getPhoneSearchFilterValue(filter: string): string | undefined {
  const digitsOnlyFilter = filter.replace(PHONE_FORMATTING_CHARACTERS_REGEX, "");

  if (!/^\d+$/.test(digitsOnlyFilter) || digitsOnlyFilter.length < MIN_PHONE_SEARCH_DIGIT_COUNT) {
    return undefined;
  }

  if (digitsOnlyFilter.length === 11 && digitsOnlyFilter.startsWith(US_COUNTRY_CODE)) {
    return digitsOnlyFilter.slice(1);
  }

  return digitsOnlyFilter;
}

function getTeamMemberSearchFilter(filter: string | undefined): TeamMemberSearchFilter {
  const trimmedFilter = filter?.trim() ?? "";

  if (trimmedFilter.includes("@")) {
    return { email: trimmedFilter };
  }

  const phoneFilterValue = getPhoneSearchFilterValue(trimmedFilter);
  if (isDefined(phoneFilterValue)) {
    return { phone: phoneFilterValue };
  }

  if (isEmailSearchFilter(trimmedFilter)) {
    return { email: trimmedFilter };
  }

  return { name: trimmedFilter };
}

function permissionsWithInHomeDefaults(
  workplaceType: string,
  permissions: string[] | undefined
): string[] | undefined {
  if (
    !["Home Healthcare", "Hospice"].includes(workplaceType) ||
    permissions?.includes(FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE)
  ) {
    return permissions;
  }

  if (isDefined(permissions)) {
    return [...permissions, FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE];
  }

  return [FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE];
}

export function useTeamMembersApiHook({
  setIsDebouncing,
}: {
  setIsDebouncing: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const {
    userId,
    admin,
    adminUserId,
    type,
    profile: { userId: workplaceId },
  } = useSelector((state: SessionType) => state.session);
  const profileDispatch = useDispatch();
  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [teamMemberState, dispatch] = useReducer(teamMemberReducer, teamMemberInitialState);
  const [addTeamMembersOnboardingUpdateStatus, setAddTeamMembersOnboardingUpdateStatus] =
    useState<AddTeamMembersOnboardingUpdateStatus>("idle");

  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  // Monotonic id for the most recently requested `fetchTeamMembers` call.
  // Switching tabs (Active ↔ Deactivated) fires concurrent fetches; without
  // a guard the response that resolves *last* wins, so a stale in-flight
  // archived request (which returns an empty list) can clobber the active
  // list that the user is now looking at. Each call captures its id and only
  // dispatches if it's still the latest.
  const latestFetchIdRef = useRef(0);
  const getUsersMutation = useMutation({
    mutationFn: fetchUsers,
  });
  const getUsersCountMutation = useMutation({ mutationFn: fetchUserCount });
  const getUserWithEmail = useMutation({ mutationFn: findUserWithEmail });
  const setLinkNewUser = useMutation({ mutationFn: linkNewUser });
  const setUpdateUser = useMutation({ mutationFn: updateUserInfo });
  const setCreateUser = useMutation({ mutationFn: createNewUser });

  const flags = useFlags();

  const generateUserInfo = async (
    formData: Partial<ITeamMember>,
    member?: ITeamMember
  ): Promise<[boolean, Partial<TypeUserInfo>]> => {
    let userInfo: Partial<ITeamMember> = {};
    if (formData && member) {
      // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'ITeamMember'.
      userInfo = omitBy(formData, (value, key) => isEqual(member[key], value));
    } else {
      if (formData) {
        userInfo = formData;
      }
    }
    const granularControl = fillDefaultAlertSettings(
      member?.alertSettings ?? { EMAIL: {}, SMS: {} },
      formData.roles,
      true,
      flags,
      formData.permissions
    ) as GranularControl;

    const generatedUserInfo = createNotifyRoles(userInfo, granularControl);
    const updateProperties = omit(generatedUserInfo ?? {}, "alertSettings", "notify");

    return [!!Object.keys(updateProperties), generatedUserInfo];
  };

  const isLoading = getUsersMutation.isLoading || getUsersCountMutation.isLoading;
  const hasLoadedTeamMembers = getUsersMutation.isSuccess && getUsersCountMutation.isSuccess;

  const cancel = async (
    formData: Partial<ITeamMember>,
    member: ITeamMember,
    user: SessionType["session"]["user"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    admin: SessionType["session"]["admin"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    type: SessionType["session"]["type"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    workplaceId: SessionType["session"]["profile"]["userId"]
  ) => {
    const role = formData.roles as unknown as string;
    const updatedData = { ...formData, roles: [role] };
    const [hasChanged, userInfo] = await generateUserInfo(updatedData, member);

    if (hasChanged) {
      const eventData = generateAddEditLogData(userInfo, user, admin, type, workplaceId, member);
      logEvent(HCF_USER_EVENTS.ADD_EDIT_TEAM_MEMBER, {
        ...eventData,
        action: "close",
      });
    }
  };

  const fetchTeamMembers = async ({
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    userId,
    isArchived,
    page,
    filter,
    resetData = false,
  }: {
    userId: string;
    isArchived: boolean;
    page?: number;
    filter?: string;
    resetData?: boolean;
  }) => {
    setIsDebouncing(false);

    const fetchId = (latestFetchIdRef.current += 1);

    try {
      const searchFilter = getTeamMemberSearchFilter(filter);

      const [count, members] = await Promise.all([
        getUsersCountMutation.mutateAsync({
          filter: searchFilter,
          facilityId: userId,
          isArchived,
        }),
        getUsersMutation.mutateAsync({
          filter: searchFilter,
          page,
          facilityId: userId,
          isArchived,
        }),
      ]);

      // A newer fetch (e.g. a tab switch) superseded this one while it was in
      // flight — drop the stale result so it can't overwrite the current list.
      if (fetchId !== latestFetchIdRef.current) {
        return;
      }

      dispatch({ type: "SET_TEAM_MEMBERS_COUNT", count });

      if (members?.length) {
        dispatch({
          type: "SET_TEAM_MEMBERS",
          teamMembers: members,
          resetData,
        });
      } else {
        dispatch({ type: "CLEAR_TEAM_MEMBERS" });
      }
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      if (fetchId !== latestFetchIdRef.current) {
        return;
      }
      showErrorToast("Something went wrong when fetching members");
    }
  };

  const fetchUserWithEmail = async (email: string, roles: string[]) => {
    try {
      const member = await getUserWithEmail.mutateAsync({
        facilityId: userId,
        email,
      });

      if (!isDefined(member?._id)) {
        return;
      }

      if (getHighestAccessRole(member.roles) > getHighestAccessRole(roles)) {
        return;
      }

      dispatch({
        type: "SET_MODAL_MEMBER",
        member: {
          ...member,
          role: getHighestAccessRole(member?.roles, true),
        },
      });
    } catch (err) {
      showErrorToast(getErrorMessage(err));
    }
  };

  const linkUser = async (memberId: string) => {
    const event = generateLogData(userId, admin, adminUserId, type, workplaceId, memberId);
    try {
      await setLinkNewUser.mutateAsync({
        userId: memberId,
        facilityId: userId ?? "",
        signInLink: teamMemberState.member
          ? getSignInLink(routes.root, teamMemberState.member.email)
          : undefined,
      });
      logEvent(HCF_USER_EVENTS.LINK_TEAM_MEMBER, {
        ...event,
        success: true,
      });
    } catch (err) {
      showErrorToast(getErrorMessage(err));
      logEvent(HCF_USER_EVENTS.LINK_TEAM_MEMBER, {
        ...event,
        success: false,
      });
    }
  };

  const updateUser = async (
    formData: Partial<ITeamMember>,
    member: ITeamMember,
    user: SessionType["session"]["user"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    admin: SessionType["session"]["admin"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    adminUserId: SessionType["session"]["adminUserId"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    type: SessionType["session"]["type"],
    facilityId: string
  ) => {
    const [hasChanged, userInfo] = await generateUserInfo(formData, member);
    const eventData = generateAddEditLogData(userInfo, user, admin, type, facilityId, member);

    try {
      const form = {
        facilityId,
        performedBy: admin ? adminUserId : (user?._id ?? ""),
        userId: member?._id,
        userInfo,
      };
      await setUpdateUser.mutateAsync(form);
      if (hasChanged) {
        logEvent(HCF_USER_EVENTS.ADD_EDIT_TEAM_MEMBER, {
          ...eventData,
          action: "edit",
          success: true,
        });
      }
    } catch (err) {
      showErrorToast(getErrorMessage(err));
      if (hasChanged) {
        logEvent(HCF_USER_EVENTS.ADD_EDIT_TEAM_MEMBER, {
          ...eventData,
          action: "edit",
          success: false,
        });
      }
    }
  };

  const createUser = async (
    formData: Partial<ITeamMember>,
    user: SessionType["session"]["user"],
    profile: SessionType["session"]["profile"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    admin: SessionType["session"]["admin"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    adminUserId: SessionType["session"]["adminUserId"],
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    type: SessionType["session"]["type"],
    facilityId: string
  ) => {
    const [hasChanged, userInfo] = await generateUserInfo(formData);
    const eventData = generateAddEditLogData(userInfo, user, admin, type, facilityId);
    try {
      const form = {
        facilityId,
        performedBy: admin ? adminUserId : (user?._id ?? ""),
        user: {
          ...userInfo,
          permissions: permissionsWithInHomeDefaults(profile.type, userInfo.permissions),
        },
        signInLink: isDefined(userInfo.email)
          ? getSignInLink(routes.root, userInfo.email)
          : undefined,
      };
      await setCreateUser.mutateAsync(form);

      if (profile.onboardingSteps?.addTeamMembers === false) {
        profileDispatch({
          type: UPDATE_PROFILE,
          data: {
            ...profile,
            onboardingSteps: {
              ...profile.onboardingSteps,
              addTeamMembers: true,
            },
          },
        });
      }
      if (hasChanged) {
        logEvent(HCF_USER_EVENTS.ADD_EDIT_TEAM_MEMBER, {
          ...eventData,
          action: "add",
          success: true,
        });
      }
    } catch (err) {
      showErrorToast(getErrorMessage(err));
      if (hasChanged) {
        logEvent(HCF_USER_EVENTS.ADD_EDIT_TEAM_MEMBER, {
          ...eventData,
          action: "add",
          success: false,
        });
      }
    }
  };

  const { mutateAsync: updateOnboardingSteps } = useMutation({
    mutationFn: ({
      workplaceIdToUpdate,
      steps,
      workplaceUserId,
    }: {
      workplaceIdToUpdate: string;
      steps: Partial<FacilityOnboardingStep>;
      workplaceUserId: string;
    }) => updateFacilityOnboardingSteps(workplaceIdToUpdate, steps, workplaceUserId),
  });

  const handleAddTeamMemberOnboardingStep = async (
    workplaceUserId: string,
    profile: SessionType["session"]["profile"]
  ) => {
    if (
      profile.onboardingSteps?.addTeamMembers === false &&
      addTeamMembersOnboardingUpdateStatus !== "pending" &&
      addTeamMembersOnboardingUpdateStatus !== "succeeded"
    ) {
      setAddTeamMembersOnboardingUpdateStatus("pending");
      try {
        const resp = await updateOnboardingSteps({
          workplaceIdToUpdate: profile.userId,
          steps: {
            addTeamMembers: true,
          },
          workplaceUserId,
        });
        setAddTeamMembersOnboardingUpdateStatus("succeeded");
        profileDispatch({
          type: UPDATE_PROFILE,
          data: { ...profile, onboardingSteps: resp },
        });
      } catch {
        setAddTeamMembersOnboardingUpdateStatus("failed");
      }
    }
  };

  return {
    teamMembers,
    setTeamMembers,
    currentPage,
    setCurrentPage,
    fetchTeamMembers,
    nextPageToken,
    setNextPageToken,
    isLoading,
    getUsersMutation,
    hasLoadedTeamMembers,
    fetchUserWithEmail,
    linkUser,
    updateUser,
    createUser,
    dispatch,
    teamMemberState,
    cancel,
    handleAddTeamMemberOnboardingStep,
    addTeamMembersOnboardingUpdateStatus,
  };
}
